'use client';

import Grid from '@mui/material/Grid';
import IconButton from '@mui/material/IconButton';
import Tab from '@mui/material/Tab';
import Tooltip, { tooltipClasses } from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import { styled } from '@mui/material/styles';
import { InfoCircleIcon } from '@startengine/design-system.ui.icons';
import { Tabs } from '@startengine/design-system.ui.tabs';
import { colors } from '@startengine/design-system.theme.main';
import PropTypes from 'prop-types';
import { useState } from 'react';
import Box from '@mui/material/Box';

import useIsMobile from 'hooks/useIsMobile';

export const BodyContainer = styled('div')(({ theme }) => ({
  paddingLeft: '24px',
  paddingRight: '24px',
  maxWidth: '100%',
  boxSizing: 'border-box',
  overflowX: 'hidden',
  [theme.breakpoints.down('sm')]: {
    paddingLeft: '16px',
    paddingRight: '16px',
  },
}));

export const StyledTabs = styled(Tabs)(({ theme }) => ({
  maxWidth: 740,
  position: 'relative',
  '.MuiButtonBase-root': {
    padding: '14px 10px 0px 10px !important',
    minWidth: 'auto !important',
    minHeight: '44px',
  },
  '.MuiTabs-scroller': {
    overflowX: 'auto !important',
    width: '100%',
    maskImage: 'linear-gradient(to right, black 85%, transparent 100%)',
    WebkitMaskImage: 'linear-gradient(to right, black 85%, transparent 100%)',
    scrollbarWidth: 'none',
    '&::-webkit-scrollbar': {
      display: 'none',
    },
  },
  '.MuiTabs-flexContainer': {
    flexWrap: 'nowrap',
  },
  '.MuiTabs-indicator': {
    display: 'none',
  },
  [theme.breakpoints.up('sm')]: {
    '.MuiButtonBase-root': {
      padding: '14px 19px 0px 19px !important',
    },
    '.MuiTabs-scroller': {
      overflowX: 'hidden !important',
      width: '100%',
      maskImage: 'none',
      WebkitMaskImage: 'none',
    },
  },
}));

export const StyledTab = styled(Tab)(({ theme }) => ({
  fontSize: '12px !important',
  lineHeight: '16px !important',
  textTransform: 'uppercase !important',
  padding: '14px 10px 14px 10px !important',
  minWidth: 'auto !important',
  minHeight: '44px !important',
  whiteSpace: 'nowrap',
  [theme.breakpoints.up('sm')]: {
    fontSize: '14px !important',
    padding: '14px 19px 0px 19px !important',
  },
}));

export const CampaignMainGrid = styled(Grid)(({ theme }) => ({
  maxWidth: '1198px',
  margin: '30px auto 100px auto',
  width: '100%',
  boxSizing: 'border-box',
  overflowX: 'hidden',
  [theme.breakpoints.down('sm')]: {
    margin: '16px auto 60px auto',
  },
  [theme.breakpoints.up('sm')]: {
    padding: '0 !important',
    margin: '20px auto 100px auto',
  },
}));

const CustomTooltips = styled(({ className, ...rest }) => (
  <Tooltip
    {...rest}
    classes={{
      popper: className,
    }}
  />
))(() => ({
  [`& .${tooltipClasses.tooltip}`]: {
    backgroundColor: colors.SEBlack80,
    color: colors.white,
    backdropFilter: 'blur(20px)',
    borderRadius: '12px',
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'flex-start',
    padding: '16px',
    maxWidth: '240px',
  },
  [`& .${tooltipClasses.arrow}`]: {
    color: colors.SEBlack80,
  },
}));

export const CustomTooltip = ({
  content = '',
  color = '#666666',
  iconSize = '14px',
  size = 'small',
  contentTypographySx = {},
  'data-cy': dataCy,
  ...rest
}) => {
  const [showTooltip, setShowTooltip] = useState(false);
  const isMobile = useIsMobile();

  return (
    <CustomTooltips
      arrow
      PopperProps={{
        modifiers: [
          {
            name: 'offset',
            options: {
              offset: [0, -17],
            },
          },
        ],
      }}
      {...(isMobile
        ? {
            disableTouchListener: true,
          }
        : {})}
      onClick={() => setShowTooltip(!showTooltip)}
      open={showTooltip}
      onOpen={() => setShowTooltip(true)}
      onClose={() => setShowTooltip(false)}
      placement="top"
      enterTouchDelay={0}
      title={
        <Typography
          color="inherit"
          sx={{
            fontWeight: '500',
            fontSize: '14px',
            lineHeight: '16px',
            textAlign: 'center',
            ...contentTypographySx,
          }}
        >
          {content}
        </Typography>
      }
      {...rest}
    >
      <IconButton
        sx={{
          width: iconSize || '14px',
          height: iconSize || '14px',
          marginTop: '0px',
          marginLeft: '4px',
        }}
        size={size}
        data-cy={dataCy}
      >
        <InfoCircleIcon color={color} />
      </IconButton>
    </CustomTooltips>
  );
};

CustomTooltip.propTypes = {
  content: PropTypes.string,
  color: PropTypes.string,
  iconSize: PropTypes.string,
  size: PropTypes.string,
};

export const ModalListItem = styled('div')(() => ({
  alignItems: 'center',
  cursor: 'pointer',
  display: 'flex',
  flexDirection: 'row',
  height: '64px',
  justifyContent: 'space-between',
}));

export const ModalListTitle = styled(Typography)(({ theme }) => ({
  fontWeight: '500',
  fontSize: '16px',
  lineHeight: '20px',
  color: theme.palette.common.black,
}));

export const StyledContentContainer = styled(Box)(({ theme }) => ({
  wordBreak: 'break-word',
  '*': {
    whiteSpace: 'normal !important',
  },
  maxWidth: '100%',
  pre: {
    whiteSpace: 'pre-wrap',
    wordWrap: 'break-word',
  },
  video: {
    maxWidth: '100%',
    height: 'auto',
  },
  iframe: {
    maxWidth: '100%',
    minWidth: {
      lg: 640,
    },
  },
  img: {
    maxWidth: '100%',
    height: 'auto',
  },
  fontSize: '14px',
  fontWeight: 400,
  lineHeight: '20px',
  letterSpacing: '0.05px',
  paddingBottom: '80px',
  [theme.breakpoints.up('sm')]: {
    paddingBottom: '0px',
  },
}));
